import { useEffect, useState } from "react";
import { Printer, FileDown, Mail, History } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";

export const DEFAULT_MAIL_MSG = `Sayın Yetkili,

COP31 projesi kapsamındaki sipariş özeti aşağıda bilginize sunulmuştur.

Sorularınız için bizimle iletişime geçebilirsiniz.

Saygılarımızla,
COP31 Rate Card Ekibi`;

export const Kpi = ({ icon: Icon, label, value, testId }) => (
  <div className="border border-stone-200 bg-stone-50 p-3" data-testid={testId}>
    <div className="flex items-center justify-between">
      <p className="text-[10px] uppercase tracking-[0.15em] text-stone-500">{label}</p>
      <Icon size={13} className="text-[#1C4E35]" strokeWidth={1.8} />
    </div>
    <p className="font-heading mt-1 text-xl font-black tracking-tight text-[#1C4E35]">{value}</p>
  </div>
);

export const EmailHistory = ({ scope, refresh, testPrefix }) => {
  const [logs, setLogs] = useState(null);
  useEffect(() => {
    if (!scope) return;
    api.get("/email-log", { params: { scope_key: scope } }).then((r) => setLogs(r.data)).catch(() => setLogs([]));
  }, [scope, refresh]);
  if (!scope || logs === null) return null;
  return (
    <div className="border border-stone-200 bg-stone-50 px-3 py-2" data-testid={`${testPrefix}-email-history`}>
      <p className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-stone-500">
        <History size={11} /> Gönderim Geçmişi
      </p>
      {logs.length === 0 ? (
        <p className="mt-1 text-[11px] text-stone-400" data-testid={`${testPrefix}-email-history-empty`}>Henüz e-posta gönderilmedi.</p>
      ) : (
        <ul className="mt-1.5 max-h-28 space-y-1 overflow-y-auto">
          {logs.map((l, i) => (
            <li key={i} className="flex flex-wrap items-center gap-x-2 text-[11px] text-stone-600" data-testid={`${testPrefix}-email-history-row-${i}`}>
              <span className="font-medium text-[#1C4E35]">{l.to}</span>
              <span className="text-stone-400">{l.sent_at ? format(new Date(l.sent_at), "dd.MM.yyyy HH:mm") : ""}</span>
              <span className="border border-stone-300 bg-white px-1 py-px text-[9px]">{l.include_prices === false ? "Fiyatsız" : "Fiyatlı"}</span>
              <span className="border border-stone-300 bg-white px-1 py-px text-[9px]">{l.mode === "new" ? "Sadece Yeniler" : "Hepsi"}</span>
              {l.sent_by && <span className="text-stone-400">· {l.sent_by}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const Toggle = ({ value, onChange, options, testPrefix }) => (
  <div className="flex overflow-hidden border border-stone-300">
    {options.map(([val, label, tid]) => (
      <button
        key={String(val)}
        type="button"
        onClick={() => onChange(val)}
        data-testid={`${testPrefix}-${tid}`}
        className={`flex-1 px-2 py-1.5 text-xs font-medium transition-colors ${value === val ? "bg-[#1C4E35] text-white" : "bg-white text-stone-600 hover:bg-stone-100"}`}
      >
        {label}
      </button>
    ))}
  </div>
);

export const DetailActions = ({ pdfPath, pdfParams, emailPath, emailPayload, filename, testPrefix, historyScope, compact, onMailSent, showMode = true, showDeliveredFilter = false }) => {
  const [busy, setBusy] = useState("");
  const [mailOpen, setMailOpen] = useState(false);
  const [to, setTo] = useState("");
  const [message, setMessage] = useState(DEFAULT_MAIL_MSG);
  const [includePrices, setIncludePrices] = useState(false);
  const [mode, setMode] = useState("all");
  const [pdfDialog, setPdfDialog] = useState(null);
  const [pdfPrices, setPdfPrices] = useState(false);
  const [pdfDelivered, setPdfDelivered] = useState("all");
  const [sentCount, setSentCount] = useState(0);

  const openPdfDialog = (action) => {
    setPdfPrices(false);
    setPdfDelivered(pdfParams?.only_delivered ? "marked" : "all");
    setPdfDialog(action);
  };

  const buildPdfParams = (withPrices, extra = {}) => {
    const { only_delivered, ...rest } = pdfParams || {};
    const p = { ...rest, include_prices: withPrices, ...extra };
    if (showDeliveredFilter) {
      if (pdfDelivered !== "all") p.delivered_filter = pdfDelivered;
    } else if (only_delivered) {
      p.only_delivered = only_delivered;
    }
    return p;
  };

  const fetchPdf = async (withPrices) => {
    const res = await api.get(pdfPath, { params: buildPdfParams(withPrices), responseType: "blob" });
    const cd = res.headers["content-disposition"] || "";
    let name = null;
    const m = cd.match(/filename\*=UTF-8''([^;]+)/i);
    if (m) name = decodeURIComponent(m[1]);
    else {
      const m2 = cd.match(/filename="?([^";]+)/i);
      if (m2) name = m2[1];
    }
    return { blob: new Blob([res.data], { type: "application/pdf" }), name };
  };

  const runPdfAction = async () => {
    const action = pdfDialog;
    setPdfDialog(null);
    setBusy(action);
    try {
      if (action === "pdf") {
        const { blob, name } = await fetchPdf(pdfPrices);
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = name || (typeof filename === "function" ? filename() : filename);
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("PDF indirildi");
      } else {
        const qs = new URLSearchParams();
        Object.entries(buildPdfParams(pdfPrices, { inline: 1 })).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== "") qs.set(k, v);
        });
        window.open(`${api.defaults.baseURL}${pdfPath}?${qs.toString()}`, "_blank");
        toast.success("Yazdırma görünümü açıldı");
      }
    } catch {
      toast.error(action === "pdf" ? "PDF oluşturulamadı" : "Yazdırma başarısız");
    } finally {
      setBusy("");
    }
  };

  const sendMail = async () => {
    setBusy("mail");
    try {
      await api.post(emailPath, { ...emailPayload, to, message, include_prices: includePrices, mode });
      toast.success(`Özet ${to} adresine gönderildi`);
      setMailOpen(false);
      setSentCount((c) => c + 1);
      onMailSent?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const btnCls = compact
    ? "h-7 w-7 rounded-sm border-stone-300 p-0 hover:bg-stone-100 active:scale-95"
    : "h-8 rounded-sm border-stone-300 text-xs hover:bg-stone-100 active:scale-95";

  return (
    <>
      <div className={`flex flex-wrap items-center ${compact ? "gap-1" : "gap-2"}`}>
        <Button variant="outline" size="sm" onClick={() => openPdfDialog("print")} disabled={busy === "print"} title="Yazdır" data-testid={`${testPrefix}-print-button`} className={btnCls}>
          <Printer size={13} className={compact ? "" : "mr-1.5"} /> {compact ? "" : "Yazdır"}
        </Button>
        <Button variant="outline" size="sm" onClick={() => openPdfDialog("pdf")} disabled={busy === "pdf"} title="PDF İndir" data-testid={`${testPrefix}-pdf-button`} className={btnCls}>
          <FileDown size={13} className={compact ? "" : "mr-1.5"} /> {compact ? "" : busy === "pdf" ? "Hazırlanıyor..." : "PDF İndir"}
        </Button>
        {emailPath && (
          <Button size="sm" onClick={() => setMailOpen(true)} title="E-posta Gönder" data-testid={`${testPrefix}-mail-button`} className={compact ? "h-7 w-7 rounded-sm bg-[#1C4E35] p-0 text-white hover:bg-[#143A26] active:scale-95" : "h-8 rounded-sm bg-[#1C4E35] text-xs text-white hover:bg-[#143A26] active:scale-95"}>
            <Mail size={13} className={compact ? "" : "mr-1.5"} /> {compact ? "" : "E-posta Gönder"}
          </Button>
        )}
      </div>

      <Dialog open={!!pdfDialog} onOpenChange={(v) => !v && setPdfDialog(null)}>
        <DialogContent aria-describedby={undefined} className="rounded-sm bg-white sm:max-w-xs" data-testid={`${testPrefix}-pdf-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-bold tracking-tight">
              {pdfDialog === "print" ? "Yazdırma Seçenekleri" : "PDF Seçenekleri"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-stone-600">Fiyatlar</Label>
            <Toggle
              value={pdfPrices}
              onChange={setPdfPrices}
              testPrefix={testPrefix}
              options={[[true, "Fiyatlı", "pdf-prices-on"], [false, "Fiyatsız", "pdf-prices-off"]]}
            />
            <p className="text-[11px] text-stone-400">Fiyatsız seçilirse tutar sütunu ve toplamlar belgeye eklenmez.</p>
          </div>
          {showDeliveredFilter && (
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-stone-600">Teslim Durumu</Label>
              <Toggle
                value={pdfDelivered}
                onChange={setPdfDelivered}
                testPrefix={testPrefix}
                options={[["all", "Tümü", "pdf-delivered-all"], ["marked", "Sadece Seçilmişler", "pdf-delivered-marked"], ["unmarked", "Seçilmemişler", "pdf-delivered-unmarked"]]}
              />
              <p className="text-[11px] text-stone-400">Sadece Seçilmişler: teslim edildi işaretli ürünler · Seçilmemişler: işaretsiz ürünler.</p>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setPdfDialog(null)} className="rounded-sm border-stone-300">Vazgeç</Button>
            <Button onClick={runPdfAction} data-testid={`${testPrefix}-pdf-confirm-button`} className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95">
              {pdfDialog === "print" ? "Yazdır" : "İndir"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={mailOpen} onOpenChange={setMailOpen}>
        <DialogContent aria-describedby={undefined} className="rounded-sm bg-white sm:max-w-md" data-testid={`${testPrefix}-mail-dialog`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-base font-bold tracking-tight">Özeti E-posta ile Gönder</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-stone-600">Alıcı E-posta</Label>
              <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="ornek@firma.com" data-testid={`${testPrefix}-mail-to-input`} className="rounded-sm border-stone-300" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-stone-600">Mesaj (e-posta gövdesinde yer alır)</Label>
              <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={6} data-testid={`${testPrefix}-mail-message-input`} className="rounded-sm border-stone-300 text-sm" />
            </div>
            <div className={`grid gap-3 ${showMode ? "grid-cols-2" : "grid-cols-1"}`}>
              {showMode && (
                <div className="space-y-1.5">
                  <Label className="text-xs uppercase tracking-wider text-stone-600">İçerik</Label>
                  <Toggle value={mode} onChange={setMode} testPrefix={testPrefix} options={[["all", "Hepsi", "mail-mode-all"], ["new", "Sadece Yeniler", "mail-mode-new"]]} />
                </div>
              )}
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-stone-600">Fiyatlar</Label>
                <Toggle value={includePrices} onChange={setIncludePrices} testPrefix={testPrefix} options={[[true, "Fiyatlı", "mail-prices-on"], [false, "Fiyatsız", "mail-prices-off"]]} />
              </div>
            </div>
            {showMode && mode === "new" && <p className="text-[11px] text-amber-700">Sadece bu özet için yapılan son e-posta gönderiminden sonra eklenen siparişler dahil edilir.</p>}
            <p className="text-[11px] text-stone-400">Mesajın altına özet tablosu otomatik eklenir.</p>
            {historyScope && mailOpen && <EmailHistory scope={historyScope} refresh={sentCount} testPrefix={`${testPrefix}-dialog`} />}
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMailOpen(false)} className="rounded-sm border-stone-300">Vazgeç</Button>
            <Button onClick={sendMail} disabled={busy === "mail" || !to} data-testid={`${testPrefix}-mail-send-button`} className="rounded-sm bg-[#1C4E35] text-white hover:bg-[#143A26] active:scale-95">
              {busy === "mail" ? "Gönderiliyor..." : "Gönder"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
